import {
  DATABASE_ROW_SHARD_CONDENSE_DUE_FIELD,
  DATABASE_ROW_SHARD_CONDENSE_MAP_KEY,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  planDatabaseRowOrderSplices,
  toDatabaseRowStoredCell,
} from '@streamlain/database-identity';

import { PublicationError } from '../publication';
import type {
  DatabaseCrdtEdit,
  DatabaseCrdtValue,
  DatabaseJsonValue,
  DatabaseWriterKernelSession,
} from './database-writer-kernel';
import { databaseJsonValue } from './database-writer-wire';

export interface DatabaseKernelRow {
  readonly rowId: string;
  readonly cells: Readonly<Record<string, unknown>>;
}

function equalJson(a: DatabaseJsonValue, b: DatabaseJsonValue): boolean {
  if (a === b) return true;
  if (Array.isArray(a) || Array.isArray(b))
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((value, index) => equalJson(value, b[index]))
    );
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  const left = Object.entries(a),
    right = new Map(Object.entries(b));
  return (
    left.length === right.size &&
    left.every(([key, value]) => {
      const other = right.get(key);
      return other !== undefined && equalJson(value, other);
    })
  );
}

/**
 * Asks the server follow-up of the persisted shard document to condense the
 * row fingerprints of its manifest entry (markDatabaseShardCondenseDue).
 */
export function markDatabaseKernelShardCondenseDue(
  session: DatabaseWriterKernelSession,
  docId: string
): void {
  const address = {
    collection: DATABASE_ROW_SHARD_CONDENSE_MAP_KEY,
    root: 'map' as const,
    path: [DATABASE_ROW_SHARD_CONDENSE_DUE_FIELD],
  };
  const current = session.read(docId, address);
  if (current.kind === 'value' && current.value === true) return;
  session.apply(docId, [
    { type: 'set', address, value: { kind: 'value', value: true } },
  ]);
}

const rowsAddress = (path: readonly string[]) => ({
  collection: DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  root: 'map' as const,
  path,
});
const orderAddress = {
  collection: DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  root: 'array' as const,
  path: [],
};

/** Stored cells of one row, as both row writes set them. */
function storedCells(row: DatabaseKernelRow) {
  return Object.entries(row.cells).map(
    ([id, cell]) =>
      [
        id,
        {
          kind: 'value',
          value: databaseJsonValue(toDatabaseRowStoredCell(id, cell)),
        },
      ] as const
  );
}

/**
 * Appends `rows` behind the `count` rows of a shard document that holds
 * exactly the rows it was written with, without reading them back.
 */
export function appendDatabaseKernelRows(
  session: DatabaseWriterKernelSession,
  docId: string,
  rows: readonly DatabaseKernelRow[],
  count: number
) {
  if (new Set(rows.map(row => row.rowId)).size !== rows.length)
    throw new PublicationError('FORMAT_MISMATCH');
  session.apply(docId, [
    ...rows.map(
      (row): DatabaseCrdtEdit => ({
        type: 'set',
        address: rowsAddress([row.rowId]),
        value: { kind: 'map', entries: storedCells(row) },
      })
    ),
    {
      type: 'array-splice',
      address: orderAddress,
      index: count,
      deleteCount: 0,
      insert: rows.map(
        row => ({ kind: 'value', value: row.rowId }) satisfies DatabaseCrdtValue
      ),
    },
  ]);
}

/** One engine-independent row diff. Integrated row maps retain their identity. */
export function reconcileDatabaseKernelRows(
  session: DatabaseWriterKernelSession,
  docId: string,
  rows: readonly DatabaseKernelRow[],
  captured?: { rows: DatabaseCrdtValue; order: DatabaseCrdtValue }
) {
  const address = rowsAddress;
  const current = captured?.rows ?? session.read(docId, address([]));
  const order = captured?.order ?? session.read(docId, orderAddress);
  if (current.kind !== 'map' || order.kind !== 'array')
    throw new PublicationError('FORMAT_MISMATCH');
  const previous = new Map(current.entries);
  const wanted = new Set(rows.map(row => row.rowId));
  if (wanted.size !== rows.length || rows.some(row => !row.rowId))
    throw new PublicationError('FORMAT_MISMATCH');
  const edits: DatabaseCrdtEdit[] = [];
  for (const id of previous.keys())
    if (!wanted.has(id)) edits.push({ type: 'delete', address: address([id]) });
  for (const row of rows) {
    const old = previous.get(row.rowId);
    if (old && old.kind !== 'map')
      throw new PublicationError('FORMAT_MISMATCH');
    const cells = storedCells(row);
    if (!old) {
      edits.push({
        type: 'set',
        address: address([row.rowId]),
        value: { kind: 'map', entries: cells },
      });
      continue;
    }
    const prior = new Map(old.entries);
    for (const id of prior.keys())
      if (!Object.hasOwn(row.cells, id))
        edits.push({ type: 'delete', address: address([row.rowId, id]) });
    for (const [id, cell] of cells) {
      const oldCell = prior.get(id);
      if (oldCell?.kind !== 'value' || !equalJson(oldCell.value, cell.value))
        edits.push({
          type: 'set',
          address: address([row.rowId, id]),
          value: cell,
        });
    }
  }
  const ids = order.items.map(item => {
    if (item.kind !== 'value' || typeof item.value !== 'string')
      throw new PublicationError('FORMAT_MISMATCH');
    return item.value;
  });
  for (const splice of planDatabaseRowOrderSplices(
    ids,
    rows.map(row => row.rowId)
  ))
    edits.push({
      type: 'array-splice',
      address: orderAddress,
      index: splice.index,
      deleteCount: splice.deleteCount,
      insert: splice.insert.map(
        value => ({ kind: 'value', value }) satisfies DatabaseCrdtValue
      ),
    });
  session.apply(docId, edits);
}
